<template>
  <div :id="`${source}_meetings_trends_graph`">
    <ContainerInfoItem
      :source="`${source}_meetings_trends_graph`"
      title="Meeting Trends"
      :description="range?.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <LineChart
        v-else-if="chartCategories && chartData"
        :id="`${source}_meetings_trends_chart`"
        primary-color="#A146E8"
        :categories="chartCategories"
        :series-data="chartData"
      />

      <EmptyScreen
        v-else
        :source="`${source}_meetings_trends_graph`"
        text="No data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect, onMounted } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { formatDateToMonthDay } from "@/src/utils/dateTimeUtils.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import LineChart from "@/src/components/charts/LineChart.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  source: { type: String, required: true },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const seriesColors = {
  Created: "#A146E8",
  Completed: "#48AF7D",
  Canceled: "#AC531C",
};

const processMeetingTrendsData = (data) => {
  if (!data || !data.length) {
    return { categories: null, seriesData: null };
  }

  const sortedData = [...data].sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );

  const categories = sortedData.map((item) => formatDateToMonthDay(item.date));

  const seriesData = [
    {
      name: "Created",
      data: sortedData.map((item) => item.meeting_created),
      color: seriesColors.Created,
    },
    {
      name: "Completed",
      data: sortedData.map((item) => item.meeting_completed),
      color: seriesColors.Completed,
    },
    {
      name: "Canceled",
      data: sortedData.map((item) => item.meeting_canceled),
      color: seriesColors.Canceled,
    },
  ];

  return { categories, seriesData };
};

const toFetchMeetingTrends = async () => {
  try {
    isLoading.value = true;

    const params = {
      action: "created",
      start_time: range.value?.start,
      end_time: range.value?.end,
      user_id: userIds.value,
      team_id: teamIds.value,
      meeting_agent_id: filters.value?.meeting_agent?.id,
    };

    const response = await AnalyticService.V2.fetchMeetingTrends(params);
    if (response?.success && response?.data) {
      const data = response?.data?.data;

      const { categories, seriesData } = processMeetingTrendsData(data);

      chartCategories.value = categories;
      chartData.value = seriesData;
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error("An error occurred while fetching meeting trends: ", error);
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) toFetchMeetingTrends();
});

useAnalyticsRefresh(toFetchMeetingTrends, [range, userIds, teamIds]);

onMounted(() => {
  toFetchMeetingTrends();
});
</script>
